import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { DynamicPropertyEntity } from "src/app/features/DynamicProperty/types/DynamicPropertyEntity";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import { displayDateTimeValue } from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const DynamicPropertiesTable: FC<ResourceTableProps<DynamicPropertyEntity>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<DynamicPropertyEntity>): ExtendedColumnsType<DynamicPropertyEntity>[] => {
            return [
                {
                    title: t("dynamicProperty.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicProperty.key"),
                    key: "keyProp",
                    dataIndex: "keyProp",
                    sorter: () => 0,
                    sortField: "key",
                    sortOrder: sortInfo.columnKey === "keyProp" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicProperty.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicProperty.dataType"),
                    key: "dataType",
                    dataIndex: "dataType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "dataType" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicProperty.validationPattern"),
                    key: "validationPattern",
                    dataIndex: "validationPattern",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "validationPattern" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicProperty.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: DynamicPropertyEntity) =>
        navigate(`${BASE_PATH}/${ResourceName.DYNAMIC_PROPERTIES}/${FORM_ACTION.EDIT}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<DynamicPropertyEntity>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="dynamicProperties_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: DynamicPropertyEntity) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
